import React from 'react';
import { type Node, ROOT_NODE, useEditor, useNode } from '@craftjs/core';
import { CONTENT_BLOCK_NAME, MAX_PADDING, PAGE_PHANTOM_PADDING } from '../../utils/constants';
import { clampPadding } from '../Page';
import type { TRBLNumberCOORDS } from '../../types/layout';
import { ContainerPropsDto } from '@builder4/shared/dto/craft/props/ContainerPropsDto';
import {
  getVisibilityClasses,
  DEFAULT_VISIBILITY,
  getBoxShadow,
  getPaddingStyle,
  getMarginStyle,
  getBorderRadiusStyle,
  getInnerBorderRadiusStyle,
  getPositionStyle,
  computeBackgroundImageStyle,
  getBgImageScaleProps,
  getPositionPropsV2,
  getGlobalStyleColor,
  getMediaQueryHeader,
} from '../../utils/styleHelpers';
import { useRenderContext } from '../../context/RenderContext';
import { css, cx } from '@emotion/css';
import type { CSSInterpolation, CSSObject } from '@emotion/serialize';
import type { NodeCustomProps } from '../../types/craft';
import type { StylesConfig } from '../../types/styles';
import { getBreakpointProp } from '../../utils/breakpointUtils';
import { getBgVideoEmbedSrc } from '../../utils/videoHelpers';
import {
  getBackgroundVideoContainerStyle,
  getBackgroundVideoThumbnailStyle,
  getBackgroundVideoIframeStyle,
} from '../../utils/backgroundVideoStyles';
import {
  getAnimationDataAttributes,
  getAnimationInitialClass,
  getHoverStyles,
  getStaggerDataAttribute,
} from '../../utils/animationHelpers';
import { getContinuousAnimationStyle } from '../../utils/continuousAnimationStyles';
import {
  ShapeDivider,
  resolveAutoShapeDividerColor,
  SHAPE_DIVIDER_DEFAULTS,
  type ShapeDividerType,
} from './ShapeDivider';

export type ContainerProps = ContainerPropsDto & {
  children: React.ReactNode;
};

export const CONTAINER_MAX_WIDTH = 1200;
export const defaultProps: Partial<ContainerProps> = {
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  width: '100%',
  height: 'auto',
  minHeight: 200,
  minWidth: 100,
  maxWidthUnit: 'px',
  maxWidth: CONTAINER_MAX_WIDTH,
  padding: [10, 10, 10, 10],
  margin: [0, 0, 0, 0],
  backgroundColor: undefined,
  borderColor: undefined,
  hoverBorderColor: undefined,
  borderWidth: 0,
  borderRadius: [0, 0, 0, 0],
  shadow: 0,
  shadowType: 'none',
  shadowSettings: { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
  opacity: 1,
  overflow: 'visible',
  bgImageScale: 100,
  bgImageOpacity: 100,
  bgImageMode: 'fill',
  bgImagePosition: 'center center',
  bgVideoSourceType: 'url',
  bgVideoUrl: undefined,
  bgVideoProvider: undefined,
  bgVideoThumbnailUrl: undefined,
  bgVideoEmbedCode: undefined,
  bgVideoMode: 'fill',
  bgVideoOpacity: 100,
  position: 'relative',
  zIndex: 0,
  fillSpace: 'no',
  flexBasis: '100%',
  flexGrow: 0,
  placeholderHeight: 512,
  positionAnchorsState: {
    enableTop: false,
    enableRight: false,
    enableBottom: false,
    enableLeft: false,
    enabled: false,
  },
  visibility: DEFAULT_VISIBILITY,
  aspectRatioLock: false,
  aspectRatio: undefined,
  disableMaxWidth: false,
  tablet: {
    padding: [
      MAX_PADDING['tablet'],
      MAX_PADDING['tablet'],
      MAX_PADDING['tablet'],
      MAX_PADDING['tablet'],
    ],
  },
  mobile: {
    padding: [
      MAX_PADDING['mobile'],
      MAX_PADDING['mobile'],
      MAX_PADDING['mobile'],
      MAX_PADDING['mobile'],
    ],
    flexDirection: 'column',
    width: '100%',
    fillSpace: 'no',
    flexBasis: '100%',
  },
};

const Container = (props: Partial<ContainerProps>) => {
  props = {
    ...defaultProps,
    ...props,
  };

  const { id } = useNode((node) => ({
    id: node.id,
  }));

  const { isEditable, environment, styles: activeGlobalStyles } = useRenderContext();

  const {
    children,
    bgImage,
    bgVideoSourceType,
    bgVideoUrl,
    bgVideoEmbedCode,
    bgVideoThumbnailUrl,
    shapeDividerType,
    shapeDividerPosition = 'bottom',
    shapeDividerColor,
    shapeDividerFrequency,
    shapeDividerFlip = false,
    shapeDividerCurveCenter,
  } = props;

  const { query } = useEditor();
  const rootNode = query.node(ROOT_NODE).get();
  const breakpoint = rootNode?.data.props.breakpoint || 'desktop';
  const overflow = getBreakpointProp(props, 'overflow', breakpoint);

  const hasStickyDescendant =
    !isEditable &&
    overflow === 'hidden' &&
    query
      .node(id)
      .descendants(true)
      .some((descendantId) => {
        const descendantNode = query.node(descendantId as string).get() as Node | null;
        if (!descendantNode) return false;
        const position = getBreakpointProp(
          descendantNode.data.props as Partial<ContainerProps>,
          'position',
          breakpoint,
        );
        return position === 'sticky';
      });

  const resolvedShapeDividerColor =
    shapeDividerType && shapeDividerType !== 'none'
      ? shapeDividerColor ||
        resolveAutoShapeDividerColor(id, shapeDividerPosition, query, activeGlobalStyles.colors)
      : undefined;

  const visibilityClasses = getVisibilityClasses(props.visibility || DEFAULT_VISIBILITY);
  const bgVideoEmbedUrl = getBgVideoEmbedSrc(bgVideoSourceType, bgVideoUrl, bgVideoEmbedCode);
  const hasBackgroundVideo = Boolean(bgVideoEmbedUrl);
  const isBuilderMode = environment === 'builder';
  const isServerRender = environment === 'server';
  const renderNodeWrapper = isServerRender || environment === 'inspiration-preview';

  const hasShapeDivider = Boolean(shapeDividerType && shapeDividerType !== 'none');

  const animationClass = getAnimationInitialClass(props.animationType, environment);
  const animationAttrs = getAnimationDataAttributes({
    animationType: props.animationType,
    animationDuration: props.animationDuration,
    animationDelay: props.animationDelay,
    animationEasing: props.animationEasing,
  });
  const staggerAttrs = getStaggerDataAttribute(props.staggerDelay);

  const contentElement = (
    <div
      className={cx(
        'container-content-outer',
        getContainerContentOuterClass(props, isServerRender, activeGlobalStyles),
      )}
    >
      {bgImage && props.bgContinuousAnimation && props.bgContinuousAnimation !== 'none' ? (
        <div
          className={css({
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          })}
        >
          <div className={getBackgroundImageStyle(props, isServerRender)} />
        </div>
      ) : (
        bgImage && (
          <div
            className={cx('container-overlay', getBackgroundImageStyle(props, isServerRender))}
          />
        )
      )}
      {hasBackgroundVideo && (
        <div className={getBackgroundVideoContainerStyle(props, isServerRender)}>
          {isBuilderMode ? (
            <div className={getBackgroundVideoThumbnailStyle(props, bgVideoThumbnailUrl)} />
          ) : (
            <iframe
              src={bgVideoEmbedUrl || undefined}
              className={getBackgroundVideoIframeStyle(props)}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              title="Background video"
            />
          )}
        </div>
      )}
      {hasShapeDivider && (
        <ShapeDivider
          type={shapeDividerType as ShapeDividerType}
          position={shapeDividerPosition}
          height={getBreakpointProp(props, 'shapeDividerHeight', breakpoint) ?? 80}
          frequency={
            shapeDividerFrequency ?? SHAPE_DIVIDER_DEFAULTS[shapeDividerType as ShapeDividerType]
          }
          flip={shapeDividerFlip}
          curveCenter={shapeDividerCurveCenter ?? 50}
          color={resolvedShapeDividerColor}
          styleColors={activeGlobalStyles.colors}
          isServerRender={isServerRender}
          mobileHeight={props.mobile?.shapeDividerHeight}
          tabletHeight={props.tablet?.shapeDividerHeight}
          nodeId={id}
        />
      )}
      <div
        className={cx(
          'container-content',
          isEditable && 'node-dashed-indicator boundary-indicator',
          getContainerContentStyles(props, isServerRender, hasStickyDescendant),
        )}
        data-testid="container-content"
      >
        {children}
      </div>
    </div>
  );

  // Wrap with container-node div for server and inspiration-preview rendering
  // (in the editor, the Resizer component provides this wrapper instead)
  if (renderNodeWrapper) {
    return (
      <div
        id={id}
        className={cx(
          `container-node ${visibilityClasses}`,
          animationClass,
          getContainerNodeStyles(props, isServerRender),
        )}
        {...animationAttrs}
        {...staggerAttrs}
      >
        {contentElement}
      </div>
    );
  }

  return contentElement;
};

Container.craft = {
  displayName: CONTENT_BLOCK_NAME,
  props: defaultProps,
  custom: {
    displayName: CONTENT_BLOCK_NAME,
    isLocked: false,
  } as NodeCustomProps,
};

export default Container;

function getContainerContentOuterClass(
  props: Partial<ContainerProps>,
  isServerRender: boolean,
  activeGlobalStyles: StylesConfig,
): string {
  const backgroundColor = getGlobalStyleColor(props.backgroundColor, activeGlobalStyles.colors);
  const currentBpStyle = computeContainerContentOuterStyles(props, activeGlobalStyles);

  const finalStyle: CSSInterpolation = {
    ...currentBpStyle,
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: 'fit-content',
    pointerEvents: 'auto',
    position: 'relative',
    background: backgroundColor,
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const mobileStyles = computeContainerContentOuterStyles(
      mobile as Partial<ContainerProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles;
  }
  if (tablet) {
    const tabletStyles = computeContainerContentOuterStyles(
      tablet as Partial<ContainerProps>,
      activeGlobalStyles,
    );
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles;
  }
  return css(finalStyle);
}

function getContainerContentStyles(
  props: Partial<ContainerProps>,
  isServerRender: boolean,
  useOverflowClipForSticky: boolean = false,
) {
  const currentBpStyle = computeContainerContentStyles({
    ...props,
    padding: props.padding || [0, 0, 0, 0],
  });
  const finalStyle: CSSInterpolation = {
    ...(currentBpStyle as CSSObject),
    display: 'flex',
    width: '100%',
    height: '100%',
    minHeight: 'fit-content',
    pointerEvents: 'auto',
    position: 'relative',
  };

  if (useOverflowClipForSticky) {
    finalStyle.overflow = props.overflow || defaultProps.overflow;
    (finalStyle as CSSObject)['@supports (overflow: clip)'] = {
      overflow: 'clip',
    };
  }
  const { mobile, tablet } = props;
  if (mobile) {
    let padding = mobile.padding || props.padding;
    // Ensure we have padding guardrails
    padding = clampPadding(padding as TRBLNumberCOORDS, MAX_PADDING['mobile']);
    // Cascade border props for inner radius calculation
    const borderRadius = mobile.borderRadius || props.borderRadius;
    const borderWidth = mobile.borderWidth ?? props.borderWidth;
    const mobileStyles = computeContainerContentStyles({
      ...mobile,
      padding,
      borderRadius,
      borderWidth,
    } as Partial<ContainerProps>);
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobileStyles as CSSObject;
  }
  if (tablet) {
    let padding = tablet.padding || props.padding;
    // Ensure we have padding guardrails
    padding = clampPadding(padding as TRBLNumberCOORDS, MAX_PADDING['tablet']);
    // Cascade border props for inner radius calculation
    const borderRadius = tablet.borderRadius || props.borderRadius;
    const borderWidth = tablet.borderWidth ?? props.borderWidth;
    const tabletStyles = computeContainerContentStyles({
      ...tablet,
      padding,
      borderRadius,
      borderWidth,
    } as Partial<ContainerProps>);
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tabletStyles as CSSObject;
  }
  return css(finalStyle);
}
export function getContainerNodeStylesObject(props: Partial<ContainerProps>) {
  const baseStyle = computeContainerNodeStyles(props);
  const finalStyle = {
    ...baseStyle,
    display: 'flex',
    justifyContent: 'center',
    mobile: {},
    tablet: {},
  };

  const { mobile, tablet } = props;
  if (mobile) {
    const positionProps = getPositionPropsV2(mobile as Partial<ContainerProps>, props);
    const mobileStyles = computeContainerNodeStyles({
      ...mobile,
      ...positionProps,
    } as Partial<ContainerProps>);
    finalStyle.mobile = mobileStyles;
  }
  if (tablet) {
    const positionProps = getPositionPropsV2(tablet as Partial<ContainerProps>, props);
    const tabletStyles = computeContainerNodeStyles({
      ...tablet,
      ...positionProps,
    } as Partial<ContainerProps>);
    finalStyle.tablet = tabletStyles;
  }
  return finalStyle;
}

export function getContainerNodeStyles(props: Partial<ContainerProps>, isServerRender: boolean) {
  const { mobile, tablet, ...baseStyle } = getContainerNodeStylesObject(props);
  const finalStyle: CSSInterpolation = {
    ...baseStyle,
  };

  if (mobile) {
    finalStyle[getMediaQueryHeader(isServerRender, 'mobile')] = mobile;
  }
  if (tablet) {
    finalStyle[getMediaQueryHeader(isServerRender, 'tablet')] = tablet;
  }
  return css(finalStyle);
}

const getBackgroundImageStyle = (props: Partial<ContainerProps>, isServerRendering: boolean) => {
  const { borderRadius, borderWidth, mobile, tablet } = props;
  const currentBpStyle = computeBackgroundImageStyle({
    ...props,
    bgImageMode: props.bgImageMode || 'fill',
    bgImageOpacity: props.bgImageOpacity || 100,
    bgImageScale: props.bgImageScale || 100,
    bgImagePosition: props.bgImagePosition || 'center center',
  });

  const continuousStyle = getContinuousAnimationStyle(
    props.bgContinuousAnimation,
    props.bgContinuousSpeed,
    props.bgContinuousIntensity,
  );
  // Calculate clip-path with inner radius when border is present
  const borderWidthValue = borderWidth || 0;
  const clipPathRadiusString =
    borderRadius && borderWidthValue
      ? getInnerBorderRadiusStyle(borderRadius, borderWidthValue).borderRadius
      : borderRadius
        ? getBorderRadiusStyle(borderRadius).borderRadius
        : undefined;

  const finalStyle: CSSInterpolation = {
    ...(currentBpStyle as CSSObject),
    ...continuousStyle,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    clipPath: clipPathRadiusString ? `inset(-0.5px round ${clipPathRadiusString})` : undefined,
  };

  if (mobile) {
    const imageScaleProps = getBgImageScaleProps(props, mobile as Partial<ContainerProps>);
    const mobileStyles = computeBackgroundImageStyle({
      ...imageScaleProps,
      ...mobile,
    } as Partial<ContainerProps>);
    // Cascade border props from base if not overridden
    const mobileBorderRadius = mobile.borderRadius || borderRadius;
    const mobileBorderWidth = mobile.borderWidth ?? borderWidthValue;
    const mobileClipPathRadius =
      mobileBorderRadius && mobileBorderWidth
        ? getInnerBorderRadiusStyle(mobileBorderRadius, mobileBorderWidth).borderRadius
        : mobileBorderRadius
          ? getBorderRadiusStyle(mobileBorderRadius).borderRadius
          : undefined;
    finalStyle[getMediaQueryHeader(isServerRendering, 'mobile')] = {
      ...mobileStyles,
      ...(mobileClipPathRadius && {
        clipPath: `inset(-0.5px round ${mobileClipPathRadius})`,
      }),
    } as CSSObject;
  }
  if (tablet) {
    const imageScaleProps = getBgImageScaleProps(props, tablet as Partial<ContainerProps>);
    const tabletStyles = computeBackgroundImageStyle({
      ...imageScaleProps,
      ...tablet,
    } as Partial<ContainerProps>);
    // Cascade border props from base if not overridden
    const tabletBorderRadius = tablet.borderRadius || borderRadius;
    const tabletBorderWidth = tablet.borderWidth ?? borderWidthValue;
    const tabletClipPathRadius =
      tabletBorderRadius && tabletBorderWidth
        ? getInnerBorderRadiusStyle(tabletBorderRadius, tabletBorderWidth).borderRadius
        : tabletBorderRadius
          ? getBorderRadiusStyle(tabletBorderRadius).borderRadius
          : undefined;
    finalStyle[getMediaQueryHeader(isServerRendering, 'tablet')] = {
      ...tabletStyles,
      ...(tabletClipPathRadius && {
        clipPath: `inset(-0.5px round ${tabletClipPathRadius})`,
      }),
    } as CSSObject;
  }

  return css(finalStyle);
};

export function computeContainerContentOuterStyles(
  props: Partial<ContainerProps>,
  activeGlobalStyles: StylesConfig,
): CSSObject {
  const backgroundColor = getGlobalStyleColor(props.backgroundColor, activeGlobalStyles.colors);
  const {
    borderRadius,
    borderColor,
    hoverBorderColor,
    borderWidth,
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
    shadowType,
    shadowSettings,
  } = props;

  const borderRadiusString = borderRadius
    ? getBorderRadiusStyle(borderRadius).borderRadius
    : undefined;
  const borderWidthString = borderWidth ? `${borderWidth}px` : undefined;
  const borderColorString = getGlobalStyleColor(borderColor, activeGlobalStyles.colors);
  const hoverBorderColorString = getGlobalStyleColor(hoverBorderColor, activeGlobalStyles.colors);

  const hoverStyles = getHoverStyles(
    hoverEffect,
    hoverTransitionDuration,
    hoverIntensity,
    !!getBoxShadow(shadowType, shadowSettings),
  );
  const existingHover = hoverBorderColorString ? { borderColor: hoverBorderColorString } : {};
  const hoverEffectHover = (hoverStyles as Record<string, unknown>)['&:hover'] as
    | Record<string, unknown>
    | undefined;
  const mergedHover = { ...existingHover, ...hoverEffectHover };

  return {
    backgroundColor,
    borderRadius: borderRadiusString,
    border:
      borderWidth && borderColorString
        ? `${borderWidthString} solid ${borderColorString}`
        : borderWidth
          ? `${borderWidthString} solid transparent`
          : undefined,
    boxShadow: getBoxShadow(shadowType, shadowSettings),
    ...(hoverStyles.transition ? { transition: hoverStyles.transition } : {}),
    ...(Object.keys(mergedHover).length > 0 && {
      '&:hover': mergedHover,
    }),
  };
}

function computeContainerContentStyles(props: Partial<ContainerProps>) {
  const {
    flexDirection,
    alignItems,
    justifyContent,
    gap,
    padding,
    overflow,
    disableMaxWidth,
    margin,
    borderRadius,
    borderWidth,
  } = props;

  const paddingString = padding ? getPaddingStyle(padding as TRBLNumberCOORDS).padding : undefined;
  // Apply maximum allowed width to the inner content when fill width is on (disableMaxWidth),
  // so the content div cannot expand beyond the max limit.
  const marginSafe = margin ?? [0, 0, 0, 0];
  const computedMaxWidth = disableMaxWidth
    ? `calc(${PAGE_PHANTOM_PADDING}px - ${marginSafe[1] + marginSafe[3]}px)`
    : undefined;

  // Calculate inner border radius to prevent gap between content and rounded border
  const innerBorderRadiusString =
    borderRadius && borderWidth
      ? getInnerBorderRadiusStyle(borderRadius, borderWidth).borderRadius
      : undefined;

  return {
    flexDirection,
    justifyContent,
    alignItems,
    gap: gap !== undefined ? `${gap}px` : undefined,
    overflow,
    padding: paddingString,
    maxWidth: computedMaxWidth,
    borderRadius: innerBorderRadiusString,
  };
}

export function computeContainerNodeStyles(props: Partial<ContainerProps>) {
  const { margin, height, width, maxWidth, maxWidthUnit, fillSpace, flexBasis, zIndex } = props;
  const { position, top, left, right, bottom, positionAnchorsState } = props;
  const { aspectRatioLock, aspectRatio, disableMaxWidth } = props;
  // Get position styles using the helper function
  const positionStyle = getPositionStyle(position, top, left, right, bottom, positionAnchorsState);
  const marginString = margin ? getMarginStyle(margin as TRBLNumberCOORDS).margin : undefined;

  const isAspectRatioLocked = aspectRatioLock && aspectRatio && aspectRatio > 0;
  const aspectRatioValue = aspectRatio ? `${aspectRatio} / 1` : undefined;

  const useMaxWidth = !disableMaxWidth;
  const unit = maxWidthUnit || '%';
  const maxWidthValue = useMaxWidth && maxWidth ? `${maxWidth}${unit}` : undefined; //apply max width to the container when fillWidth is off

  let flex = undefined;
  if (fillSpace) {
    if (fillSpace === 'yes') {
      if (flexBasis) {
        flex = `1 1 ${flexBasis}`;
      }
    } else {
      flex = 'unset';
    }
  }
  return {
    flex,
    padding: marginString,
    ...positionStyle,
    zIndex: zIndex || undefined,
    width: width || undefined,
    height: isAspectRatioLocked ? 'auto' : height || undefined,
    maxWidth: maxWidthValue,
    minHeight: 'fit-content',
    ...(isAspectRatioLocked && aspectRatioValue ? { aspectRatio: aspectRatioValue } : {}),
  };
}
